import React, {useEffect, useRef, useState} from 'react';
import {alpha, makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {limparCredencial} from '../../../features/credencialPublica';
import ContainedButton from '../../shared/buttons/ContainedButton.tsx';
import useFetch from '../../../hooks/useFetch.js';
import useCreate from '../../../hooks/useCreate.js';
import useSuccessMessage from '../../../hooks/useSuccessMessage.js';
import useErrorMessage from '../../../hooks/useErrorMessage.js';
import useInfoMessage from '../../../hooks/useInfoMessage.js';
import {CpfNumberFormatCustom, DataNascimentoNumberFormatCustom} from '../../shared/formatters';
import {URL_PROCESSO_SELETIVO, URL_INSCRICAO_IDENTIFICAR} from '../../../constants';
import {mensagemDoErro} from '../../../utils/erro';
import Paper from "@material-ui/core/Paper";
import {buttonTableCell, containedButton, menuTableCell, textButton} from "../../../utils/styles.js";
import Grid from "@material-ui/core/Grid";
import LabelValue from "../../shared/labelvalue/LabelValue.js";
import Message from "../../shared/message/Message.js";
import ArrowForward from '@material-ui/icons/ArrowForwardOutlined';
import FundoCurvaUnaerp from './FundoCurvaUnaerp.jsx';
import useIsMobile from '../../../hooks/useIsMobile';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
    ...containedButton(theme, false, false),
    ...textButton(theme, false),
    ...buttonTableCell(theme, 200),
    ...menuTableCell(theme),
    pageWrapper: {
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        /* Ancora o SVG de fundo e evita que ele gere barra de rolagem lateral. */
        position: 'relative',
        overflow: 'hidden',
        /* Altura da AppBar: 56px no mobile, 64px a partir de sm. Usar min-height
           deixa a tela rolar quando o cartão não cabe, em vez de cortá-lo. */
        minHeight: 'calc(100vh - 56px)',
        padding: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
            minHeight: 'calc(100vh - 64px)',
            padding: theme.spacing(3)
        }
    },
    /* Centraliza o conteúdo na área disponível, acima do fundo em curva. */
    centro: {
        flex: 1,
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: theme.spacing(6),
        [theme.breakpoints.up('md')]: {
            justifyContent: 'space-evenly'
        }
    },
    boasVindas: {
        maxWidth: 340,
        color: theme.palette.common.white
    },
    boasVindasTitulo: {
        fontWeight: 500,
        marginBottom: theme.spacing(1)
    },
    filete: {
        width: 48,
        height: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.marca.amarelo,
        marginBottom: theme.spacing(2)
    },
    saudacao: {
        color: theme.palette.marca.textoSobreAzul,
        fontWeight: 500,
        marginBottom: theme.spacing(1)
    },
    textoBoasVindas: {
        color: theme.palette.marca.textoSecundarioSobreAzul,
        lineHeight: 1.7
    },
    root: {
        width: '100%',
        maxWidth: 520,
        boxSizing: 'border-box',
        borderRadius: theme.shape.borderRadius * 2,
        boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.16)}`,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3)
    },
    cabecalho: {
        width: '100%',
        marginBottom: theme.spacing(1)
    },
    subtitulo: {
        color: theme.palette.text.secondary
    },
    pageIdentificacao: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap',
        justifyContent: 'center',
        alignItems: 'center'
    },
    pageIdentificacaoDiv: {
        width: '100%',
        padding: '.5rem 0 .5rem 0'
    },
    button: {
        boxSizing: "borderBox",
        width: "100%"
    },
    resultContainer: {
        marginTop: theme.spacing(4),
        width: '100%'
    },
    resultPaper: {
        padding: theme.spacing(3),
        backgroundColor: theme.palette.grey[50],
        borderLeft: `6px solid ${theme.palette.primary.main}`
    },
    blocoConfirmacao: {
        marginBottom: theme.spacing(2)
    },
    valorResultado: {
        fontWeight: 'bold'
    },
    valorPositivo: {
        color: theme.palette.success.main
    },
    resultTitle: {
        color: theme.palette.primary.main,
        fontWeight: 800
    }
}));


const schema = (solicitarConfirmacao) => Yup.object({
    processoSeletivo: Yup.object().nullable().required('É necessário Processo Seletivo'),
    codigoAluno: Yup.string().when('processoSeletivo', {
        is: (ps) => ps?.programa?.id === 2,
        then: Yup.string()
            .trim()
            .required('É necessário Cód. Aluno')
            .length(6, 'O código deve ter exatamente 6 caracteres')
            .matches(/^\d+$/, 'O código deve conter apenas números'), // Somente numeros
        otherwise: Yup.string().nullable(),
    }),
    cpf: Yup.string().trim().required('É necessário CPF')
        .matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'CPF inválido. Digite os 11 dígitos'),
    dataNascimento: Yup.string().trim().required('É necessário Data Nascimento')
        .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Data inválida. Use o formato DD/MM/AAAA'),
    nome: solicitarConfirmacao ? Yup.string().trim().required('É necessário Nome Completo') : Yup.string().nullable(),
    nomeMae: solicitarConfirmacao ? Yup.string().trim().required('É necessário Nome da Mãe') : Yup.string().nullable(),
});

const defaultValues = {
    processoSeletivo: null,
    codigoAluno: '',
    cpf: '',
    dataNascimento: '',
    nome: '',
    nomeMae: '',
};

export default function PageIdentificacao({onProximo}) {
    const classes = useStyles();
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const fetch = useFetch();
    const create = useCreate();
    const successMsg = useSuccessMessage();
    const errorMsg = useErrorMessage();
    const infoMsg = useInfoMessage();
    const [processos, setProcessos] = useState([]);
    const [result, setResult] = useState(null);
    const [solicitarConfirmacao, setSolicitarConfirmacao] = useState(false);
    const [mensagemConfirmacao, setMensagemConfirmacao] = useState('');

    const {register, handleSubmit, control, watch, setValue, clearErrors, formState: {errors}} = useForm({
        resolver: yupResolver(schema(solicitarConfirmacao)),
        defaultValues,
    });

    useEffect(() => {
        // Voltar para a identificação (Sair, cancelar, sessão vencida) encerra a credencial anterior.
        limparCredencial();
        fetch(URL_PROCESSO_SELETIVO, {}, data => setProcessos(data || []));
    }, []);

    /*
     * Link do processo seletivo (admin → Processos de Seleção): ?p=ID já vem com
     * o processo escolhido. Aplica uma vez, quando a lista chega; depois o
     * candidato troca livremente.
     */
    const [searchParams] = useSearchParams();
    const processoDoLinkAplicadoRef = useRef(false);
    useEffect(() => {
        const idDoLink = Number(searchParams.get('p'));
        if (processoDoLinkAplicadoRef.current || !idDoLink || !processos.length) return;
        processoDoLinkAplicadoRef.current = true;

        const processoDoLink = processos.find(processo => processo.id === idDoLink);
        if (processoDoLink) {
            setValue('processoSeletivo', processoDoLink);
            setValue('codigoAluno', '');
            clearErrors(['processoSeletivo', 'codigoAluno']);
        } else {
            infoMsg('O processo seletivo do link não está com inscrições abertas. Selecione um processo na lista.');
        }
    }, [processos]);

    const processoSeletivo = watch('processoSeletivo');
    const exibirCodigoAluno = processoSeletivo?.programa?.id === 2;
    /*
     * Com o código de aluno a linha passa a ter 3 colunas de ~140px e os rótulos
     * longos (que o LabelValue exibe em maiúsculas) quebram em duas linhas,
     * desalinhando os campos. Nesse caso eles entram abreviados.
     */
    const rotulo = (completo, abreviado) => (exibirCodigoAluno ? abreviado : completo);
    const cpfWatch = watch('cpf');
    const dataNascimentoWatch = watch('dataNascimento');

    useEffect(() => {
        if (solicitarConfirmacao) {
            setSolicitarConfirmacao(false);
            setMensagemConfirmacao('');
            setValue('nome', '');
            setValue('nomeMae', '');
            clearErrors(['nome', 'nomeMae']);
        }
    }, [cpfWatch, dataNascimentoWatch, processoSeletivo?.id]);

    function onSubmit(data) {
        const payload = {
            idProcessoSeletivo: data.processoSeletivo.id,
            cpf: data.cpf,
            dataNascimento: data.dataNascimento || null,
            codigoAluno: data.codigoAluno ? parseInt(data.codigoAluno, 10) : null,
            nome: data.nome || null,
            nomeMae: data.nomeMae || null
        };

        create(URL_INSCRICAO_IDENTIFICAR, payload, (response) => {
            if (response.solicitarConfirmacao) {
                setSolicitarConfirmacao(true);
                setMensagemConfirmacao(response.mensagemConfirmacao);
                infoMsg(response.mensagemConfirmacao || 'Informe o nome e o nome da mãe para continuar.');
            } else {
                setResult(response);
                /* Quem já é inscrito cai na tela da inscrição, que já diz isso. */
                if (!response.inscrito) {
                    successMsg('Identificação realizada com sucesso!');
                }
                if (onProximo) {
                    onProximo({
                        nome: payload.nome,
                        nomeMae: payload.nomeMae,
                        ...response,
                        cpf: response.cpf || payload.cpf,
                        dataNascimento: response.dataNascimento || payload.dataNascimento,
                        idProcessoSeletivo: response.idProcessoSeletivo || payload.idProcessoSeletivo
                    });
                }
            }
        }, (error) => {
            errorMsg(mensagemDoErro(error, 'Falha ao realizar identificação.'), error.mensagem || error);
        });
    }

    const handleProcessoChange = (e, onChangeForm) => {
        const selectedId = e.target.value;

        const selected = processos.find(p => p.id === selectedId) || null;
        onChangeForm(selected);

        setValue('codigoAluno', '');
        clearErrors(['codigoAluno']);
    };

    return (
        <>
            <div className={classes.pageWrapper}>
                <FundoCurvaUnaerp />
                <div className={classes.centro}>
                    <Paper className={classes.root} elevation={10}>
                        <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
                            <div className={classes.pageIdentificacao}>
                                <div className={classes.cabecalho}>
                                    <Typography variant="h5" gutterBottom>
                                        Identificação
                                    </Typography>
                                    <Typography variant="body2" className={classes.subtitulo}>
                                        Entre com os dados abaixo para login:
                                    </Typography>
                                </div>
                                <div className={classes.pageIdentificacaoDiv}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={12}>
                                            <LabelValue label='Processo Seletivo *'>
                                                <Controller
                                                    name="processoSeletivo"
                                                    control={control}
                                                    render={({field}) => (
                                                        <FormControl variant="outlined" fullWidth
                                                                     error={!!errors.processoSeletivo}>
                                                            <Select
                                                                labelId="processo-seletivo-label"
                                                                displayEmpty
                                                                value={field.value?.id || ''}
                                                                onChange={(e) => handleProcessoChange(e, field.onChange)}>
                                                                <MenuItem value=""><em>Selecione...</em></MenuItem>
                                                                {processos.map(p => (
                                                                    <MenuItem key={p.id} value={p.id}>{p.descricao}</MenuItem>
                                                                ))}
                                                            </Select>
                                                            {!!errors.processoSeletivo && (
                                                                <FormHelperText>{errors.processoSeletivo.message}</FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    )}
                                                />
                                            </LabelValue>
                                        </Grid>
                                    </Grid>
                                </div>
                                <div className={classes.pageIdentificacaoDiv}>
                                    <Grid container spacing={3}>
                                        {exibirCodigoAluno && (
                                            <Grid item xs={12} sm={4}>
                                                <LabelValue label='Cód. aluno'>
                                                    <OutlinedInput
                                                        type='text'
                                                        {...register('codigoAluno')}
                                                        error={!!errors.codigoAluno}/>
                                                    {!!errors.codigoAluno && (
                                                        <FormHelperText
                                                            error>{errors.codigoAluno.message}</FormHelperText>
                                                    )}
                                                </LabelValue>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={exibirCodigoAluno ? 4 : 6}>
                                            <LabelValue label='CPF'>
                                                <Controller
                                                    name="cpf"
                                                    control={control}
                                                    render={({field}) => (
                                                        <FormControl variant="outlined" fullWidth>
                                                            <OutlinedInput
                                                                id="cpf-input"
                                                                name={field.name}
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                onBlur={field.onBlur}
                                                                inputComponent={CpfNumberFormatCustom}
                                                                error={!!errors.cpf}
                                                            />
                                                            {!!errors.cpf &&
                                                                <FormHelperText>{errors.cpf?.message}</FormHelperText>}
                                                        </FormControl>
                                                    )}
                                                />
                                            </LabelValue>
                                        </Grid>
                                        <Grid item xs={12} sm={exibirCodigoAluno ? 4 : 6}>
                                            <LabelValue label={rotulo('Data de Nascimento', 'Nascimento')}>
                                                <Controller
                                                    name="dataNascimento"
                                                    control={control}
                                                    render={({field}) => (
                                                        <FormControl variant="outlined" fullWidth>
                                                            <OutlinedInput
                                                                id="data-nascimento-input"
                                                                name={field.name}
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                onBlur={field.onBlur}
                                                                inputComponent={DataNascimentoNumberFormatCustom}
                                                                error={!!errors.dataNascimento}
                                                            />
                                                            {!!errors.dataNascimento &&
                                                                <FormHelperText>{errors.dataNascimento?.message}</FormHelperText>}
                                                        </FormControl>
                                                    )}
                                                />
                                            </LabelValue>
                                        </Grid>
                                    </Grid>
                                </div>
                                {solicitarConfirmacao && (
                                    <div className={clsx(classes.pageIdentificacaoDiv, classes.blocoConfirmacao)}>
                                        <Message type="warning" variant="standard" marginBottom>
                                            {mensagemConfirmacao}
                                        </Message>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}>
                                                <LabelValue label='Nome Completo *'>
                                                    <OutlinedInput
                                                        type='text'
                                                        fullWidth
                                                        {...register('nome')}
                                                        error={!!errors.nome}/>
                                                    {!!errors.nome && (
                                                        <FormHelperText error>{errors.nome.message}</FormHelperText>
                                                    )}
                                                </LabelValue>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <LabelValue label='Nome da Mãe *'>
                                                    <OutlinedInput
                                                        type='text'
                                                        fullWidth
                                                        {...register('nomeMae')}
                                                        error={!!errors.nomeMae}/>
                                                    {!!errors.nomeMae && (
                                                        <FormHelperText error>{errors.nomeMae.message}</FormHelperText>
                                                    )}
                                                </LabelValue>
                                            </Grid>
                                        </Grid>
                                    </div>
                                )}
                                <div className={classes.pageIdentificacaoDiv}>
                                    <ContainedButton
                                        type="submit"
                                        startIcon={<ArrowForward />}
                                        className={classes.button}>
                                        Seguir com Inscrição
                                    </ContainedButton>
                                </div>
                            </div>
                        </form>
                        {result && (
                            <div className={classes.resultContainer}>
                                <Paper className={classes.resultPaper} elevation={3}>
                                    <Typography variant="h6" gutterBottom className={classes.resultTitle}>
                                        Resultado da Identificação
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <LabelValue label="Inscrito">
                                                <Typography variant="body1" className={clsx(classes.valorResultado, result.inscrito && classes.valorPositivo)}>
                                                    {result.inscrito ? 'Sim' : 'Não'}
                                                </Typography>
                                            </LabelValue>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <LabelValue label="Candidato Existente no Banco">
                                                <Typography variant="body1" className={classes.valorResultado}>
                                                    {result.candidatoExistente ? 'Sim' : 'Não'}
                                                </Typography>
                                            </LabelValue>
                                        </Grid>
                                        {result.dadosCadastrais?.pessoa && (
                                            <Grid item xs={12}>
                                                <LabelValue label="Nome do Candidato">
                                                    <Typography variant="body1" className={classes.valorResultado}>
                                                        {result.dadosCadastrais.pessoa.nome || 'NÃO PREENCHIDO'}
                                                    </Typography>
                                                </LabelValue>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Paper>
                            </div>
                        )}
                    </Paper>

                    {/* Sobre o azul da curva; no celular a curva entra sozinha, sem o texto. */}
                    {!isMobile && (
                        <div className={classes.boasVindas}>
                            <Typography variant="h4" className={classes.boasVindasTitulo}>
                                Bem-vindo
                            </Typography>
                            <div className={classes.filete} />
                            <Typography variant="body1" className={classes.saudacao}>
                                Prezado(a) Estudante,
                            </Typography>
                            <Typography variant="body2" className={classes.textoBoasVindas}>
                                Bem vindo(a) a área de inscrições para editais de bolsas e preenchimento
                                do formulário de solicitação de Alongamento de mensalidades da AERP,
                                mantenedora da Unaerp e do Colégio Técnico da AERP.
                            </Typography>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
